/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, useTheme, alpha, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  TableSortLabel,
  Pagination,
  LinearProgress,
  Alert,
  Snackbar,
  SnackbarCloseReason,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useParams, useSearchParams } from 'react-router-dom';
import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import NavTabs from './NavTabs';
import { ProdutosService } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import { Environment } from '../../../shared/environment';
import { LinhasEsqueletoTabela } from '../../../shared/components';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

dayjs.locale('pt-br');
dayjs.extend(utc);

// Interfaces para os dados
interface OperacaoEntrada {
  dataoperacao: string;
  numero: string;
  nome: string;
  usuario: string;
  qtde: number;
  preco: number;
  total: number;
  tipo: 'entrada';
}

interface OperacaoSaida {
  dataoperacao: string;
  numero: string;
  nome: string;
  usuario: string;
  qtds: number;
  preco: number;
  total: number;
  tipo: 'saida';
}

type OperacaoItem = OperacaoEntrada | OperacaoSaida;
type SortableKeys = 'dataoperacao' | 'numero' | 'nome' | 'usuario' | 'preco' | 'total';

function isEntrada(item: OperacaoItem): item is OperacaoEntrada {
  return item.tipo === 'entrada';
}

export const MovimentoDoProduto: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const selectedItemRef = useRef<HTMLTableRowElement>(null);
  const requestIdRef = useRef(0);

  // Snackbar state
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const corEntradas = '#00ACC1';
  const corSaidas = '#42A5F5';

  const tipoOperacao = useMemo(() => {
    return (searchParams.get('tipo') as 'entrada' | 'saida') || 'saida';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const [dadosListagem, setDadosListagem] = useState<OperacaoItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState<SortableKeys>('dataoperacao');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const getQuantidade = (item: OperacaoItem): number => {
    if (isEntrada(item)) {
      return item.qtde;
    }
    return item.qtds;
  };

  const converterDadosEntrada = (dados: any[]): OperacaoEntrada[] => {
    return dados.map(item => ({
      dataoperacao: item.dataoperacao,
      numero: item.numero,
      nome: item.nome,
      usuario: item.usuario,
      qtde: item.qtde,
      preco: item.preco,
      total: item.total,
      tipo: 'entrada' as const
    }));
  };

  const converterDadosSaida = (dados: any[]): OperacaoSaida[] => {
    return dados.map(item => ({
      dataoperacao: item.dataoperacao,
      numero: item.numero,
      nome: item.nome,
      usuario: item.usuario,
      qtds: item.qtds,
      preco: item.preco,
      total: item.total,
      tipo: 'saida' as const
    }));
  };

  const fetchDados = useCallback(async () => {
    if (!id) return;
    
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setDadosListagem([]);
    
    try {
      if (tipoOperacao === 'entrada') {
        const result = await ProdutosService.getAllEntrada(pagina, id);
        
        if (requestId !== requestIdRef.current) return;
        
        if (result instanceof Error) {
          setDadosListagem([]);
          setTotalCount(0);
          setMsgType('error');
          setMsg(result.message);
          setOpenMsg(true);
        } else if (result.data && result.data.length > 0) {
          setDadosListagem(converterDadosEntrada(result.data));
          setTotalCount(result.totalCount || 0);
        } else {
          setDadosListagem([]);
          setTotalCount(0);
        }
      } else {
        const result = await ProdutosService.getAllVenda(pagina, id);
        
        if (requestId !== requestIdRef.current) return;
        if (result instanceof Error) {
          setDadosListagem([]);
          setTotalCount(0);
          setMsgType('error');
          setMsg(result.message);
          setOpenMsg(true);
        } else if (result.data && result.data.length > 0) {
          setDadosListagem(converterDadosSaida(result.data));
          setTotalCount(result.totalCount || 0);
        } else {
          setDadosListagem([]);
          setTotalCount(0);
        }
      }
      
      setSelectedIndex(0);
    } catch (error) {
      console.error('Erro na requisição:', error);
      setDadosListagem([]);
      setTotalCount(0);
      setMsgType('error');
      setMsg('Erro ao carregar os dados');
      setOpenMsg(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [tipoOperacao, pagina, id]);

  useEffect(() => {
    fetchDados();
  }, [fetchDados]);

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isLoading) return;
      
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex(prev => (prev < dadosListagem.length - 1 ? prev + 1 : prev));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dadosListagem.length, isLoading]);

  const handleRequestSort = (property: SortableKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getSortedData = () => {
    const sorted = [...dadosListagem].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      
      switch (orderBy) {
        case 'dataoperacao':
          aValue = new Date(a.dataoperacao).getTime();
          bValue = new Date(b.dataoperacao).getTime();
          break;
        case 'numero':
          aValue = a.numero;
          bValue = b.numero;
          break;
        case 'nome':
          aValue = a.nome;
          bValue = b.nome;
          break;
            case 'usuario':
          aValue = a.usuario;
          bValue = b.usuario;
          break;
        case 'preco':
          aValue = a.preco;
          bValue = b.preco;
          break;
        case 'total':
          aValue = a.total;
          bValue = b.total;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const sortedData = getSortedData();
  const selectedItem = dadosListagem[selectedIndex];

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString('pt-BR');
  };

  const handleTipoOperacaoChange = (novoTipo: 'entrada' | 'saida') => {
    setSearchParams({ tipo: novoTipo, pagina: '1' }, { replace: true });
    setSelectedIndex(0);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setSearchParams({ tipo: tipoOperacao, pagina: newPage.toString() }, { replace: true });
    setSelectedIndex(0);
  };

  return (
    <LayoutBaseDeDashboard barraDeFerramentas={<Box />}>
      <NavTabs id={id || ''} case={2} />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => handleCloseMsg()}
      >
        <Alert onClose={() => handleCloseMsg()} severity={msgType} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>

      <Box sx={{ p: 2 }}>
        {/* FILTROS */}
        <Paper sx={{ p: 1, mb: 2, borderRadius: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Operação</InputLabel>
                <Select
                  value={tipoOperacao}
                  label="Tipo de Operação"
                  onChange={(e) => handleTipoOperacaoChange(e.target.value as 'entrada' | 'saida')}
                >
                  <MenuItem value="entrada">Entradas</MenuItem>
                  <MenuItem value="saida">Venda</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 1.5, bgcolor: alpha(tipoOperacao === 'entrada' ? corEntradas : corSaidas, 0.1), borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Total de registros: 
                  <strong style={{ color: tipoOperacao === 'entrada' ? corEntradas : corSaidas, marginLeft: '4px' }}>
                    {totalCount}
                  </strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>


        {/* LISTAGEM DE OPERAÇÕES */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              Listagem de {tipoOperacao === 'entrada' ? 'Entradas' : 'Saídas'}
              <Chip label={`${dadosListagem.length} itens`} size="small" sx={{ ml: 1 }} />
            </Typography>
            
            <Box display="flex" gap={1}>
              <Chip icon={<KeyboardArrowUpIcon />} label="Seta ↑" size="small" variant="outlined" />
              <Chip icon={<KeyboardArrowDownIcon />} label="Seta ↓" size="small" variant="outlined" />
            </Box>
          </Box>

          <TableContainer>
            <Table stickyHeader size="small">
              {!listaCelular && (
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'dataoperacao'} direction={orderBy === 'dataoperacao' ? order : 'asc'} onClick={() => handleRequestSort('dataoperacao')}>Data</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'numero'} direction={orderBy === 'numero' ? order : 'asc'} onClick={() => handleRequestSort('numero')}>Número</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'nome'} direction={orderBy === 'nome' ? order : 'asc'} onClick={() => handleRequestSort('nome')}>Nome</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'usuario'} direction={orderBy === 'usuario' ? order : 'asc'} onClick={() => handleRequestSort('usuario')}>Vendedor</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'preco'} direction={orderBy === 'preco' ? order : 'asc'} onClick={() => handleRequestSort('preco')}>Preço</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    <TableSortLabel active={orderBy === 'total'} direction={orderBy === 'total' ? order : 'asc'} onClick={() => handleRequestSort('total')}>Total</TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              )}
              {isLoading && dadosListagem.length === 0 && (
                <TableBody>
                  <LinhasEsqueletoTabela
                    colunas={[
                      { width: '30%' },
                      { width: '75%' },
                      { variant: 'rounded', width: 60, height: 22 },
                      { width: '80%' },
                      { width: '70%' },
                      { width: '50%', align: 'right' },
                      { width: '55%', align: 'right' },
                      { width: '55%', align: 'right' },
                    ]}
                  />
                </TableBody>
              )}
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <LinearProgress sx={{ height: '2px' }} />
                    </TableCell>
                  </TableRow>
                ) : dadosListagem.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        Nenhum dado encontrado para {tipoOperacao === 'entrada' ? 'entradas' : 'saídas'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={sortedData}
                  chave={(row) => `${row.numero}-${row.dataoperacao}`}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nome || '?'}
                      cor={tipoOperacao === 'entrada' ? corEntradas : corSaidas}
                      titulo={row.nome}
                      detalhes={[formatDate(row.dataoperacao), `Nº ${row.numero}`, row.usuario, `Qtd ${formatNumber(getQuantidade(row))} × ${formatNumber(row.preco)}`]}
                      valor={formatNumber(row.total)}
                      corValor={tipoOperacao === 'entrada' ? corEntradas : corSaidas}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : sortedData.map((item, idx) => {
                    const isSelected = idx === selectedIndex;
                    const quantidade = getQuantidade(item);
                    
                    return (
                      <TableRow
                        key={`${item.numero}-${item.dataoperacao}`}
                        ref={isSelected ? selectedItemRef : null}
                        onClick={() => setSelectedIndex(idx)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                          borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                        }}
                      >
                        <TableCell>{((pagina - 1) * Environment.LIMITE_DE_LINHASSMALL) + idx + 1}</TableCell>
                        <TableCell>{formatDate(item.dataoperacao)}</TableCell>
                        <TableCell><Chip label={item.numero} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} /></TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{item.nome}</Typography></TableCell>
                        <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{item.usuario}</Typography></TableCell>
                        <TableCell align="right"><Typography fontWeight="bold">{formatNumber(quantidade)}</Typography></TableCell>
                        <TableCell align="right">{formatNumber(item.preco)}</TableCell>
                        <TableCell align="right"><Typography fontWeight="bold" color={tipoOperacao === 'entrada' ? corEntradas : corSaidas}>{formatNumber(item.total)}</Typography></TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > Environment.LIMITE_DE_LINHASSMALL && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, pt: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Pagination
                page={pagina}
                count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHASSMALL)}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                shape="rounded"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
          
          {totalCount > 0 && (
            <Box textAlign="center" mt={1}>
              <Typography variant="caption" color="text.secondary">
                Total de {totalCount} registro{totalCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </LayoutBaseDeDashboard>
  );
};